/**
 * The project shell — everything every project screen shares (`KLQ-20260819-004`).
 *
 * Chrome (rail + top bar) comes from `AppChrome`; this adds the three things chat1.md §4 and
 * `KLQ-20260827-002` draw inside a project and nowhere else: the **phase bar**, the **tab
 * row**, and the **context bar** carrying the project's name. It ships with C10 because C10 is
 * the first project screen, and it ships as a component every later screen composes with
 * rather than as People's private layout (retro rule 8).
 *
 * **The phase bar is drawn whenever the project is, and the tab row is not.** The tab row can
 * legitimately be empty — a caller whose every surface is a later wave gets no tabs at all —
 * while the phase bar is drawn even for a project with one phase, because otherwise "+ add
 * phase" is invisible to precisely the studio that has never used phases. So the two are
 * separate slots on `AppFrame` rather than one, and the scroll offset is composed from both.
 *
 * **The free-text phase chip is gone.** `project.phase` was a `str | None` the practice typed
 * by hand; it is now a row in `project_phase` with an order, a norm behind it and a bar of its
 * own, and there is nothing left for a chip beside the project name to say that the bar above
 * does not say better.
 *
 * **The tab row comes off the wire, not off a role the client guessed.**
 * `GET /projects/{id}/surfaces` answers it — see `tabs.ts` for the two states a missing tab
 * can be in and why they must look different.
 *
 * Two failure shapes, deliberately distinct:
 *
 * - The project query fails with a `404` — which is also what a caller with no relationship
 *   to the project gets, because the backend refuses to confirm it exists (invariant 4). The
 *   shell says "no such project" and offers the way back; it never says "forbidden", because
 *   the server was careful not to.
 * - The surfaces query fails while the project loads. Then there is no tab row and the
 *   screen renders bare rather than guessing a row, which is the fail-closed direction: a
 *   guessed row can only ever invent a door.
 */

import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

import { ProjectTabs } from '@/components/chrome';
import type { ProjectTabKey } from '@/components/chrome';
import { useMessages } from '@/lib/i18n';

import { HierarchyProvider } from '@/features/drawings';

import { AppChrome } from './AppChrome';
import { usePhaseContext } from './phase';
import { ProjectPhaseBar } from './ProjectPhaseBar';
import styles from './ProjectShell.module.css';
import { useProject, useProjectSurfaces } from './queries';
import { tabsFor } from './tabs';

export interface ProjectShellProps {
  readonly projectId: string;
  /** Which tab is current, or `null` on the index route while it decides where to go. */
  readonly tab: ProjectTabKey | null;
  readonly children: ReactNode;
}

export function ProjectShell({ projectId, tab, children }: ProjectShellProps) {
  const t = useMessages('app');
  const project = useProject(projectId);
  const surfaces = useProjectSurfaces(projectId);

  const name = project.data?.name ?? '';
  const tabs = tabsFor(surfaces.data, tab);
  // Read here rather than inside `ProjectTabs` so that the row stays a component that draws
  // what it is handed. It is the same cached hook the bar itself reads — no second request.
  const { selectedId } = usePhaseContext(projectId);

  if (project.isError) {
    return (
      <AppChrome crumbs={[t('project.missing.title')]}>
        <div className={styles.state}>
          <h1 className={styles.stateTitle}>{t('project.missing.title')}</h1>
          <p className={styles.stateBody}>{t('project.missing.body')}</p>
          <Link to="/projects" className={styles.back}>
            <ArrowLeft size={14} strokeWidth={1.5} aria-hidden />
            <span>{t('project.action.backToProjects')}</span>
          </Link>
        </div>
      </AppChrome>
    );
  }

  return (
    <AppChrome
      crumbs={[t('chrome.nav.projects'), name]}
      phaseBar={<ProjectPhaseBar projectId={projectId} />}
      {...(tabs.length === 0
        ? {}
        : {
            tabs: (
              <ProjectTabs
                tabs={tabs}
                name={name}
                projectId={projectId}
                {...(selectedId === null ? {} : { phase: selectedId })}
              />
            ),
          })}
    >
      {/* The project's name is in the tab row (`KLQ-20260910-001`). It had a header of its
          own here — 24px of padding and a 30px line on every project screen — repeating the
          word the breadcrumb above it already carries. On the tasks screen that put four
          bands of chrome between the tab row and the first row of the table. */}
      {/* The tree is held here, once, for every screen under this shell (`KLQ-20260820-005`
          §3). Not around the missing-project return above: a project that does not load has no
          tree to ask for. */}
      <HierarchyProvider projectId={projectId}>{children}</HierarchyProvider>
    </AppChrome>
  );
}

/**
 * What a caller sees inside a project whose every screen is a later wave — today an engineer,
 * a contractor or a client, all of whom may reach the project and none of whose surfaces are
 * built (§3).
 *
 * It is a real panel and not a redirect: sending them back to the list would make a project
 * they are legitimately on look like one they cannot open.
 */
export function ProjectNothingBuilt() {
  const t = useMessages('app');
  return (
    <div className={styles.state}>
      <h2 className={styles.stateTitle}>{t('project.empty.title')}</h2>
      <p className={styles.stateBody}>{t('project.empty.body')}</p>
      <Link to="/projects" className={styles.back}>
        <ArrowLeft size={14} strokeWidth={1.5} aria-hidden />
        <span>{t('project.action.backToProjects')}</span>
      </Link>
    </div>
  );
}
